import { useEffect, useState } from "react";
import {
  DeleteIcon,
  EditIcon,
  IconButton,
  Tooltip,
  useToast,
} from "@songtradr-one/components-frontend";
import { errorText } from "../../statics/error-text";
import {
  AddEntryButton,
  EmptyStateContainer,
  Entry,
  EntryActions,
  EntryItem,
  EntryLabel,
  EntryListContainer,
  EntryListContent,
  EntryListHeader,
  EntryListHeaderSection,
  EntryListProps,
  EntryListSubtitle,
  EntryListTitle,
  text,
} from "./EntryList.Parts";

export const EntryList = <T extends Record<string, unknown> = Entry>({
  title,
  subtitle,
  initialEntries = [],
  onAddEntries,
  onReplaceEntries,
  addActions,
  onEditEntry,
  onEntriesChange,
  addButtonContent = text.defaultAddButtonText,
  removeConfirmation,
  disabled = false,
  labelProperty = "label" as keyof T,
  displayValue,
  emptyText,
  actions,
}: EntryListProps<T>) => {
  const { addToast } = useToast();
  const [entries, setEntries] = useState<T[]>(initialEntries);
  const [isLoading, setIsLoading] = useState(false);

  // Update internal state when initialEntries changes (from form controller)
  useEffect(() => {
    setEntries(initialEntries as T[]);
  }, [initialEntries]);

  const updateEntries = (newEntries: T[]) => {
    setEntries(newEntries);
    onEntriesChange?.(newEntries);
  };

  const handleAddEntries = async (
    onAdd: () => Promise<T[] | void> | T[] | void,
  ) => {
    if (disabled) return;

    setIsLoading(true);
    try {
      const result = await onAdd();
      if (result && Array.isArray(result)) {
        const newEntries = [...entries, ...(result as T[])];
        updateEntries(newEntries);
      }
    } catch {
      addToast({ text: errorText.default.message, variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplaceEntries = async () => {
    if (disabled) return;

    if (!onReplaceEntries) return;

    setIsLoading(true);
    try {
      const result = await onReplaceEntries();
      if (result && Array.isArray(result)) {
        updateEntries(result as T[]);
      }
    } catch {
      addToast({ text: errorText.default.message, variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEntry = async (index: number, entry: T) => {
    if (!onEditEntry || disabled) return;

    setIsLoading(true);
    try {
      const result = await onEditEntry(entry, index);
      if (result) {
        const newEntries = [...entries];
        newEntries[index] = result as T;
        updateEntries(newEntries);
      }
    } catch {
      addToast({ text: errorText.default.message, variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveEntry = async (index: number, entry: T) => {
    if (disabled) return;

    // Check for confirmation if provided
    if (removeConfirmation) {
      const confirmed = await removeConfirmation(entry);
      if (!confirmed) return;
    }

    // Remove from internal state
    const newEntries = entries.filter((_, i) => i !== index);
    updateEntries(newEntries);
  };

  return (
    <>
      {(title || subtitle) && (
        <EntryListHeaderSection>
          {title && <EntryListTitle>{title}</EntryListTitle>}
          {subtitle && <EntryListSubtitle>{subtitle}</EntryListSubtitle>}
        </EntryListHeaderSection>
      )}
      <EntryListContainer>
        <EntryListHeader>
          {addActions
            ? addActions.map((action) => (
                <AddEntryButton
                  key={action.key}
                  type="button"
                  $variant="subtle"
                  $size="small"
                  disabled={disabled || isLoading}
                  onClick={() => handleAddEntries(action.onAdd)}
                >
                  {isLoading ? text.updating : action.content}
                </AddEntryButton>
              ))
            : (onAddEntries || onReplaceEntries) && (
                <AddEntryButton
                  type="button"
                  $variant="subtle"
                  $size="small"
                  disabled={disabled || isLoading}
                  onClick={
                    onAddEntries
                      ? () => handleAddEntries(onAddEntries)
                      : onReplaceEntries
                        ? handleReplaceEntries
                        : undefined
                  }
                >
                  {isLoading ? text.updating : addButtonContent}
                </AddEntryButton>
              )}
        </EntryListHeader>

        <EntryListContent>
          {entries.length === 0 && emptyText ? (
            <EmptyStateContainer>{emptyText}</EmptyStateContainer>
          ) : (
            entries.map((entry, index) => (
              <EntryItem
                key={`entry-${index}`}
                $clickable={!!onEditEntry && !disabled && !isLoading}
                onClick={() =>
                  onEditEntry &&
                  !disabled &&
                  !isLoading &&
                  handleEditEntry(index, entry)
                }
              >
                <EntryLabel>
                  {String(entry[labelProperty])}
                  {displayValue && <span>{displayValue(entry)}</span>}
                </EntryLabel>
                <EntryActions>
                  {actions?.map((action, actionIndex) => (
                    <Tooltip key={`action-${actionIndex}`} tip={action.title}>
                      <IconButton
                        $small
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick(entry);
                        }}
                        disabled={disabled || isLoading}
                      >
                        {action.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                  {onEditEntry && (
                    <Tooltip tip={text.editTooltip}>
                      <IconButton
                        $small
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEntry(index, entry);
                        }}
                        disabled={disabled || isLoading}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip tip={text.deleteTooltip}>
                    <IconButton
                      $small
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveEntry(index, entry);
                      }}
                      disabled={disabled}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </EntryActions>
              </EntryItem>
            ))
          )}
        </EntryListContent>
      </EntryListContainer>
    </>
  );
};
